import { useCallback } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { ArrowBackOutline, PlusOutline, SidebarLeftOutline } from '@lyearn/icons';
import { IconButton, InfoPill, Stack, Tooltip } from '@lyearn/ui';
import { NoteReviewStatusTypeEnum } from '@/types/schema';

import { NoteFragment } from '../../graphql/queries/generated/notes';
import { KnowledgeDocsTabs, NotesViewMode } from '../../types';
import AskForApprovalButton from './askForApprovalButton';
import ManageApprovalButtons from './manageApprovalButtons';

interface KnowledgeBaseHeaderProps {
  onNew: () => void;
  onToggleCollapsed: () => void;
  showNotesOnly?: boolean;
  note?: NoteFragment | null;
  setCurrentNote?: React.Dispatch<React.SetStateAction<NoteFragment | null>>;
  setHideNoteButtons?: React.Dispatch<React.SetStateAction<boolean>>;
  hideNoteButtons?: boolean;
  showBackButton?: boolean;
  hideCollapseButton?: boolean;
  plusIconRight?: boolean;
  viewMode?: NotesViewMode;
  currentNotesTab?: KnowledgeDocsTabs;
}

export const KnowledgeBaseHeader = ({
  onNew,
  note,
  setCurrentNote,
  showBackButton,
  onToggleCollapsed,
  setHideNoteButtons,
  hideNoteButtons,
  showNotesOnly = false,
  hideCollapseButton = false,
  plusIconRight = false,
  viewMode,
  currentNotesTab,
}: KnowledgeBaseHeaderProps) => {
  const { t } = useTranslation();
  const handleBackButtonClick = useCallback(() => {
    setHideNoteButtons?.(false);
    setCurrentNote?.(null);
  }, [setCurrentNote, setHideNoteButtons]);

  const renderHeaderButtons = () => {
    if (viewMode === NotesViewMode.Notes) {
      return null;
    }
    if (
      currentNotesTab === KnowledgeDocsTabs.MyDocs &&
      note &&
      (!note.reviewConfig?.reviewStatus ||
        note.reviewConfig?.reviewStatus === NoteReviewStatusTypeEnum.Rejected)
    ) {
      return <AskForApprovalButton note={note} setCurrentNote={setCurrentNote!} />;
    }
    if (currentNotesTab === KnowledgeDocsTabs.PendingApproval && note) {
      return <ManageApprovalButtons note={note} setCurrentNote={setCurrentNote!} />;
    }
    if (
      currentNotesTab === KnowledgeDocsTabs.MyDocs &&
      note &&
      note.reviewConfig?.reviewStatus === NoteReviewStatusTypeEnum.InReview
    ) {
      return <InfoPill appearance="blue" label="In Review" type="text" />;
    }
    if (note && note.reviewConfig?.reviewStatus === NoteReviewStatusTypeEnum.Approved) {
      return <InfoPill appearance="success" label="Approved" type="text" />;
    }
    return null;
  };

  return (
    <Stack
      alignItems="center"
      className="w-full -mb-6"
      justifyContent={plusIconRight ? 'end' : 'between'}>
      {showBackButton ? (
        <Stack alignItems="center" className="w-full" direction="row" justifyContent="between">
          <IconButton size="m" onClick={handleBackButtonClick}>
            <ArrowBackOutline height={24} width={24} />
          </IconButton>
          {hideNoteButtons ? null : (
            <Stack alignItems="center" direction="row" gap={12} justifyContent="end">
              {renderHeaderButtons()}
            </Stack>
          )}
        </Stack>
      ) : (
        <IconButton size="m" onClick={onNew}>
          <PlusOutline />
        </IconButton>
      )}
      {hideCollapseButton ? null : (
        <Tooltip placement="right" title={t('Common:CollapseSidebar')}>
          <IconButton size="m" onClick={onToggleCollapsed}>
            <SidebarLeftOutline />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
};
